import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Level {
  level: number;
  subLevels: number;
  minValue: number;
  maxValue: number;
  maxBars: number;
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;
const SEGMENTS_PER_BAR = 10;

function fmtDecimal(value: number): string {
  return value.toFixed(1).replace('.', ',');
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function mountLearnDecimals(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const totalSubs = level.subLevels;
  let subIndex = 0;
  let correctCount = 0;
  // target em décimos (inteiro pra evitar float compare)
  let targetTenths = 0;
  // estado: pra cada barra, quantos segmentos pintados (do topo)
  let bars: number[] = [];
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Aprenda os decimais</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage ldec">
      <div class="feedback" aria-live="polite"></div>
      <p class="ldec__progress" aria-live="polite"></p>
      <div class="ldec__target" aria-live="polite"></div>
      <p class="ldec__current" aria-live="polite"></p>
      <div class="ldec__bars"></div>
      <div class="ldec__actions">
        <button class="btn btn--secondary ldec__reset" type="button">↺ Limpar</button>
        <button class="btn btn--primary ldec__confirm" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.ldec__progress')!;
  const targetEl = page.querySelector<HTMLElement>('.ldec__target')!;
  const currentEl = page.querySelector<HTMLElement>('.ldec__current')!;
  const barsEl = page.querySelector<HTMLElement>('.ldec__bars')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.ldec__reset')!;
  const confirmBtn = page.querySelector<HTMLButtonElement>('.ldec__confirm')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  resetBtn.addEventListener('click', () => {
    if (buttonsBlocked) return;
    bars = new Array(level.maxBars).fill(0);
    ctx.audio.play('click');
    renderAll();
  });
  confirmBtn.addEventListener('click', validate);

  function startSubLevel(): void {
    const minTenths = Math.round(level.minValue * 10);
    const maxTenths = Math.round(level.maxValue * 10);
    targetTenths = randInt(minTenths, maxTenths);
    bars = new Array(level.maxBars).fill(0);
    buttonsBlocked = false;
    renderAll();
  }

  function currentTenths(): number {
    return bars.reduce((a, b) => a + b, 0);
  }

  function renderAll(): void {
    renderProgress();
    renderTarget();
    renderCurrent();
    renderBars();
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${subIndex + 1} de ${totalSubs}`;
  }

  function renderTarget(): void {
    targetEl.innerHTML = `Represente o número: <strong>${fmtDecimal(targetTenths / 10)}</strong>`;
  }

  function renderCurrent(): void {
    currentEl.textContent = `Pintado: ${fmtDecimal(currentTenths() / 10)}`;
  }

  function renderBars(): void {
    barsEl.innerHTML = '';
    for (let i = 0; i < level.maxBars; i++) {
      const bar = document.createElement('div');
      bar.className = 'ldec__bar';
      // Segmentos de cima pra baixo: o de cima é o "1.0", o de baixo é "0.1"
      // Lógica: bars[i] = quantos pintados (do BAIXO pro cima)
      for (let s = SEGMENTS_PER_BAR; s >= 1; s--) {
        const seg = document.createElement('div');
        seg.className = 'ldec__seg';
        if (s <= bars[i]!) seg.classList.add('ldec__seg--filled');
        seg.textContent = `${s / 10},`.replace('.', ',').replace(/,$/, '').replace(/(\d)/, '0,$1');
        // Simplificar label: mostrar 0,1 0,2 ... 1,0
        seg.textContent = (s / 10).toFixed(1).replace('.', ',');
        seg.dataset['bar'] = String(i);
        seg.dataset['seg'] = String(s);
        seg.addEventListener('click', () => onSegmentClick(i, s));
        bar.appendChild(seg);
      }
      barsEl.appendChild(bar);
    }
  }

  function onSegmentClick(barIdx: number, segLevel: number): void {
    if (buttonsBlocked) return;
    if (bars[barIdx]! >= segLevel) {
      // Despinta tudo do segLevel pra cima (mantém abaixo)
      bars[barIdx] = segLevel - 1;
    } else {
      // Pinta de baixo até segLevel
      bars[barIdx] = segLevel;
    }
    ctx.audio.play('click');
    renderCurrent();
    renderBars();
  }

  function validate(): void {
    if (buttonsBlocked) return;
    if (currentTenths() === targetTenths) {
      buttonsBlocked = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      subIndex++;
      window.setTimeout(() => {
        if (subIndex < totalSubs) startSubLevel();
        else finishLevel();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalSubs} decimais.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/learn_decimals/${level.level + 1}`;
      });
    }
  }

  startSubLevel();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
